import type React from 'react';
import { useId, useRef } from 'react';

import { AGENT_NAME_MAX, AGENT_NOTE_MAX, type PresenceAnswer, toggleAnswer } from './agent-presence';

type PresencePick = Exclude<PresenceAnswer, 'unrecorded'>;

const OPTIONS: { value: PresencePick; label: string; glyph: string }[] = [
	{ value: 'present', label: 'Agent present', glyph: '✓' },
	{ value: 'absent', label: 'No agent', glyph: '–' },
];

/**
 * One party's polling-agent answer: a two-pill radio group plus, when present, the name as
 * written in column four of the sheet.
 *
 * Neither pill selected is the third state, `unrecorded`, and tapping the selected pill
 * returns to it — so a mis-tap is undone in place without a third control.
 */
export const PresenceToggle = ({
	partyLabel,
	answer,
	agentName,
	note,
	isOwnParty,
	showNote = false,
	onAnswer,
	onAgentName,
	onNote,
}: {
	partyLabel: string;
	answer: PresenceAnswer;
	agentName: string;
	note?: string;
	isOwnParty?: boolean;
	/** The review stage shows a note field; the vote card keeps to the essentials. */
	showNote?: boolean;
	onAnswer: (next: PresenceAnswer) => void;
	onAgentName: (name: string) => void;
	onNote?: (note: string) => void;
}) => {
	const uid = useId();
	const refs = useRef<(HTMLButtonElement | null)[]>([]);
	const selectedIndex = OPTIONS.findIndex((o) => o.value === answer);

	// Roving tabindex: one tab stop for the group, arrows move AND select (WAI radio pattern).
	const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
		e.preventDefault();
		const next = (index + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + OPTIONS.length) % OPTIONS.length;
		onAnswer(OPTIONS[next].value);
		refs.current[next]?.focus();
	};

	return (
		<div className={`pa-toggle pa-toggle--${answer}`}>
			<div className='pa-toggle__row'>
				<span
					className='pa-toggle__label'
					id={`${uid}-label`}
				>
					Polling agent
					{isOwnParty ? <span className='pa-toggle__own'>Your party</span> : null}
				</span>
				<div
					className='pa-toggle__group'
					role='radiogroup'
					aria-labelledby={`${uid}-label`}
					aria-describedby={`${uid}-hint`}
				>
					{OPTIONS.map((option, index) => {
						const checked = answer === option.value;
						const tabbable = selectedIndex === -1 ? index === 0 : checked;
						return (
							<button
								key={option.value}
								ref={(el) => {
									refs.current[index] = el;
								}}
								type='button'
								role='radio'
								aria-checked={checked}
								aria-label={`${option.label} for ${partyLabel}`}
								tabIndex={tabbable ? 0 : -1}
								className={`pa-pill pa-pill--${option.value}${checked ? ' is-selected' : ''}`}
								onClick={() => onAnswer(toggleAnswer(answer, option.value))}
								onKeyDown={(e) => onKeyDown(e, index)}
							>
								<span
									className='pa-pill__glyph'
									aria-hidden='true'
								>
									{option.glyph}
								</span>
								<span className='pa-pill__text'>{option.label}</span>
							</button>
						);
					})}
				</div>
			</div>
			{answer === 'unrecorded' ? (
				<span
					className='pa-toggle__hint'
					id={`${uid}-hint`}
				>
					Not recorded yet
				</span>
			) : (
				// Radios cannot normally be unchecked; tell assistive tech that this one can.
				<span
					className='visually-hidden'
					id={`${uid}-hint`}
				>
					Select the chosen option again to clear it.
				</span>
			)}
			{answer === 'present' ? (
				<input
					type='text'
					className='pa-toggle__name'
					placeholder='Agent name as on the sheet (optional)'
					aria-label={`Name of ${partyLabel} polling agent`}
					value={agentName}
					maxLength={AGENT_NAME_MAX}
					autoComplete='off'
					onChange={(e) => onAgentName(e.target.value)}
				/>
			) : null}
			{showNote && answer !== 'unrecorded' ? (
				<textarea
					className='pa-toggle__note'
					placeholder='Note (optional) — e.g. arrived late, refused to sign'
					aria-label={`Note about ${partyLabel} polling agent`}
					value={note || ''}
					maxLength={AGENT_NOTE_MAX}
					rows={2}
					onChange={(e) => onNote?.(e.target.value)}
				/>
			) : null}
		</div>
	);
};

export default PresenceToggle;
